/**
 * @file index.tsx
 *
 * @fileoverview Shows search metadata information as metatags on the page.
 */
import React, { useContext } from 'react';
import { Metatag } from '~/jobiqo/types/types';
import Head from 'next/head';
import { useConfig } from '~/jobiqo/context/config';
import { PathContext } from '~/jobiqo/context/app';
import { PathContextInterface } from '~/jobiqo/models/app';
import {
  CANONICAL_QUERY_PARAM_EXCLUDE_PATTERNS,
  excludeCanonicalPathQueryParamsByPattern,
} from '~/jobiqo/components/Search/utils/searchHelpers';

export interface SearchMetatagsProps {
  metadata: Metatag[];
}

export const SearchMetatags = ({ metadata }: SearchMetatagsProps) => {
  const { getConfigByName } = useConfig();
  const pathContext: PathContextInterface = useContext(PathContext);
  const noindexPaginatedSearchPages: boolean = !!getConfigByName(
    'noindex_paginated_search_pages',
  )?.value[0];

  const page = pathContext.querystring?.page
    ? parseInt(pathContext.querystring?.page, 10)
    : 1;
  const isPaginatedPage = Number.isInteger(page) && page > 1;
  const forceNoindex = noindexPaginatedSearchPages && isPaginatedPage;

  return (
    metadata && (
      <Head>
        {metadata.map((metaInfo) => {
          switch (metaInfo.meta) {
            case 'page_title':
              return <title key="title">{metaInfo.value}</title>;
            case 'description':
              return (
                metaInfo.value && (
                  <meta
                    key={metaInfo.meta}
                    name={metaInfo.meta}
                    content={metaInfo.value}
                  />
                )
              );
            case 'keywords':
              return (
                metaInfo.value && (
                  <meta
                    key={metaInfo.meta}
                    name={metaInfo.meta}
                    content={metaInfo.value}
                  />
                )
              );
            case 'robots':
              return (
                !forceNoindex &&
                metaInfo.value && (
                  <meta
                    key={metaInfo.meta}
                    name={metaInfo.meta}
                    content={metaInfo.value}
                  />
                )
              );
            case 'canonical':
              return (
                metaInfo.value && (
                  <link
                    rel="canonical"
                    href={excludeCanonicalPathQueryParamsByPattern(
                      metaInfo.value,
                      CANONICAL_QUERY_PARAM_EXCLUDE_PATTERNS,
                    )}
                    key="canonical"
                  />
                )
              );
            default:
              return null;
          }
        })}
        {forceNoindex && <meta key="robots" name="robots" content="noindex" />}
      </Head>
    )
  );
};
